import {
  Box,
  Button,
  Dialog,
  Theme,
  Typography,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import { useBackdropStyles, CustomBackdrop } from '@terragotech/gen5-shared-components';
import React from 'react';

interface Props {
  showModal: boolean;
  handleAction: (value: 'confirm' | 'cancel') => void;
  description?: string;
  title?: string;
  confirmationText?: string;
  cancellationText?: string;
  dialogContainerStyle?: React.CSSProperties;
}

const ConfirmModal = ({
  showModal,
  handleAction,
  description = 'The mapping will be cleared.',
  title = 'Are you sure?',
  confirmationText = 'Delete',
  cancellationText = 'Cancel',
  dialogContainerStyle,
}: Props) => {
  const classes = useStyles();
  const backdropClasses = useBackdropStyles();

  const handleSubmit = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    handleAction('confirm');
  };

  const onCancel = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    handleAction('cancel');
  };

  const BackdropWithStopPropagation = (props: any) => (
    <CustomBackdrop {...props} onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => e.stopPropagation()} />
  );

  return (
    <Dialog
      open={showModal}
      onClose={undefined}
      transitionDuration={{ exit: 0, enter: 100 }}
      maxWidth="xl"
      PaperProps={{ style: dialogContainerStyle || styles.dialogStyle }}
      BackdropComponent={BackdropWithStopPropagation}
      BackdropProps={{
        classes: { root: backdropClasses.backdrop },
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Typography className={classes.header}>{title}</Typography>
      <Typography className={classes.label}>{description}</Typography>
      <Box className={classes.footerRow}>
        <Button variant="contained" onClick={onCancel} className={classes.cancel} disableElevation>
          {cancellationText}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          disableElevation
          onClick={handleSubmit}
          className={classes.saveButton}
        >
          {confirmationText}
        </Button>
      </Box>
    </Dialog>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      fontSize: 18,
      color: theme.palette.text.primary,
      fontWeight: 600,
      paddingBottom: 12,
    },
    label: {
      fontSize: 14,
      fontWeight: 400,
      color: theme.palette.text.secondary,
      paddingBottom: 26,
      lineHeight: 'normal',
    },
    footerRow: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: 9,
      height: 32,
    },
    cancel: {
      background: theme.palette.action.disabledBackground,
      color: theme.palette.text.primary,
      padding: '7px 20px',
      fontSize: 13,
      fontStyle: 'normal',
      fontWeight: 500,
      borderRadius: 2,
      '&:hover': {
        background: theme.palette.action.hover,
      },
    },
    saveButton: {
      color: theme.palette.getContrastText(theme.palette.primary.main),
      padding: '7px 20px',
      fontSize: 13,
      fontStyle: 'normal',
      fontWeight: 500,
      backgroundColor: theme.palette.primary.main,
      borderRadius: 2,
      '&:hover': {
        color: theme.palette.getContrastText(theme.palette.primary.main),
        backgroundColor: theme.palette.primary.dark,
      },
    },
  })
);

const styles = {
  dialogStyle: {
    width: 520,
    padding: '20px',
  },
};

export default ConfirmModal;
